import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2, User, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PropertyPhotoUpload } from './property-photo-upload';
import { useFormContext, Controller } from 'react-hook-form';
import { usePropertyTypeOptions } from '@/hooks/queries/useDropdowns';

export function PropertyBasicInfo() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { control, formState } = useFormContext();

  // Fetch property types from API
  const { options: propertyTypeOptions, isLoading: isLoadingPropertyTypes } =
    usePropertyTypeOptions();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle
            className={cn(
              'flex items-center gap-2',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <Building2 className="w-5 h-5" />
            {t('properties.create.basicInfo.title')}
          </CardTitle>
          <CardDescription className={isRTL ? 'text-right' : 'text-left'}>
            {t('properties.create.basicInfo.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label
                htmlFor="name"
                className={isRTL ? 'text-right' : 'text-left'}
              >
                {t('properties.create.basicInfo.fields.name')}
              </Label>
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <Input
                    id="name"
                    {...field}
                    className={cn(
                      formState.errors.name ? 'border-destructive' : '',
                      isRTL ? 'text-right' : 'text-left'
                    )}
                    placeholder={t(
                      'properties.create.basicInfo.fields.namePlaceholder'
                    )}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="location"
                className={isRTL ? 'text-right' : 'text-left'}
              >
                {t('properties.create.basicInfo.fields.location')}
              </Label>
              <Controller
                control={control}
                name="location"
                render={({ field }) => (
                  <Input
                    id="location"
                    {...field}
                    className={cn(
                      formState.errors.location ? 'border-destructive' : '',
                      isRTL ? 'text-right' : 'text-left'
                    )}
                    placeholder={t(
                      'properties.create.basicInfo.fields.locationPlaceholder'
                    )}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                )}
              />
            </div>
          </div>

          <PropertyPhotoUpload />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label
                htmlFor="property_type_id"
                className={isRTL ? 'text-right' : 'text-left'}
              >
                {t('properties.create.basicInfo.fields.type')}
              </Label>
              <Controller
                control={control}
                name="property_type_id"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isLoadingPropertyTypes}
                  >
                    <SelectTrigger
                      className={cn(
                        formState.errors.property_type_id
                          ? 'border-destructive'
                          : '',
                        isRTL ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      <SelectValue
                        placeholder={
                          isLoadingPropertyTypes
                            ? t('loading')
                            : t(
                                'properties.create.basicInfo.fields.typePlaceholder'
                              )
                        }
                      />
                      {isLoadingPropertyTypes && (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      {propertyTypeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          <div
                            className={cn(
                              'flex items-center gap-2',
                              isRTL ? 'flex-row-reverse' : 'flex-row'
                            )}
                          >
                            <Building2 className="w-4 h-4" />
                            {option.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="stars"
                className={isRTL ? 'text-right' : 'text-left'}
              >
                {t('properties.create.basicInfo.fields.stars')}
              </Label>
              <Controller
                control={control}
                name="stars"
                render={({ field }) => (
                  <Select
                    value={String(field.value)}
                    onValueChange={v => field.onChange(parseInt(v))}
                  >
                    <SelectTrigger
                      className={cn(
                        formState.errors.stars ? 'border-destructive' : '',
                        isRTL ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      <SelectValue
                        placeholder={t(
                          'properties.create.basicInfo.fields.starsPlaceholder'
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">
                        {t('properties.create.basicInfo.starOptions.1')}
                      </SelectItem>
                      <SelectItem value="2">
                        {t('properties.create.basicInfo.starOptions.2')}
                      </SelectItem>
                      <SelectItem value="3">
                        {t('properties.create.basicInfo.starOptions.3')}
                      </SelectItem>
                      <SelectItem value="4">
                        {t('properties.create.basicInfo.starOptions.4')}
                      </SelectItem>
                      <SelectItem value="5">
                        {t('properties.create.basicInfo.starOptions.5')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle
            className={cn(
              'flex items-center gap-2',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <User className="w-5 h-5" />
            {t('properties.create.contactInfo.title')}
          </CardTitle>
          <CardDescription className={isRTL ? 'text-right' : 'text-left'}>
            {t('properties.create.contactInfo.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label
                htmlFor="contactPerson"
                className={isRTL ? 'text-right' : 'text-left'}
              >
                {t('properties.create.contactInfo.fields.contactPerson')}
              </Label>
              <Controller
                control={control}
                name="contactPerson"
                render={({ field }) => (
                  <Input
                    id="contactPerson"
                    {...field}
                    className={cn(
                      formState.errors.contactPerson
                        ? 'border-destructive'
                        : '',
                      isRTL ? 'text-right' : 'text-left'
                    )}
                    placeholder={t(
                      'properties.create.contactInfo.fields.contactPersonPlaceholder'
                    )}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="contactEmail"
                className={isRTL ? 'text-right' : 'text-left'}
              >
                {t('properties.create.contactInfo.fields.contactEmail')}
              </Label>
              <Controller
                control={control}
                name="contactEmail"
                render={({ field }) => (
                  <Input
                    id="contactEmail"
                    type="email"
                    {...field}
                    className={cn(
                      formState.errors.contactEmail ? 'border-destructive' : '',
                      isRTL ? 'text-right' : 'text-left'
                    )}
                    placeholder={t(
                      'properties.create.contactInfo.fields.contactEmailPlaceholder'
                    )}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="contactPhone"
                className={isRTL ? 'text-right' : 'text-left'}
              >
                {t('properties.create.contactInfo.fields.contactPhone')}
              </Label>
              <Controller
                control={control}
                name="contactPhone"
                render={({ field }) => (
                  <Input
                    id="contactPhone"
                    type="tel"
                    {...field}
                    className={cn(
                      formState.errors.contactPhone ? 'border-destructive' : '',
                      isRTL ? 'text-right' : 'text-left'
                    )}
                    placeholder={t(
                      'properties.create.contactInfo.fields.contactPhonePlaceholder'
                    )}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                )}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
